import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ASSETS } from '../assetManifest.js';
import { CONSTANTS } from '../data.js';

interface Props {
  onLoadingComplete: () => void;
}

export function SplashScreen({ onLoadingComplete }: Props) {
  const splash = CONSTANTS.SPLASH;
  
  // track fade out animation state
  const [shouldFadeOut, setShouldFadeOut] = useState(false);

  // lookup splash asset
  const category = splash.imageCategory;
  const key = splash.imageKey;
  const asset =
    (ASSETS &&
      (ASSETS as any)[category] &&
      (ASSETS as any)[category][key]) || {
      path: 'assets/default.png', // Fallback path
      width: 256,
      height: 256,
    };

  const width = splash.imageDisplayWidth || asset.width || 256;
  const height = splash.imageDisplayHeight || asset.height || 256;

  const bgColor = splash.backgroundColor || '#000000';
  const textColor = splash.textColor || '#ffffff';

  // grid constants from data.js
  const gridColor = splash.gridColor;
  const gridDotSize = splash.gridDotSize;
  const gridSpacing = splash.gridSpacing;

  // Start fade out animation after fadeOutDelayMs
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldFadeOut(true);
    }, splash.fadeOutDelayMs);

    return () => clearTimeout(timer);
  }, [splash.fadeOutDelayMs]);

  // Trigger onLoadingComplete after fade out animation completes
  useEffect(() => {
    if (shouldFadeOut) {
      const timer = setTimeout(() => {
        onLoadingComplete();
      }, splash.fadeOutDurationMs); // Wait for the fade out animation to finish

      return () => clearTimeout(timer);
    }
  }, [shouldFadeOut, onLoadingComplete, splash.fadeOutDurationMs]);

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center"
      style={{
        backgroundColor: bgColor,
        color: textColor,
        backgroundImage: `radial-gradient(circle, ${gridColor} ${gridDotSize}px, transparent ${gridDotSize}px)`,
        backgroundSize: `${gridSpacing}px ${gridSpacing}px`,
        backgroundPosition: '50% 50%'
      }}
    >
      <div className="flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ 
            opacity: shouldFadeOut ? 0 : 1, 
            y: 0,
            scale: shouldFadeOut ? splash.fadeOutScaleTo : 1
          }}
          transition={{ 
            duration: shouldFadeOut ? splash.fadeOutDurationMs / 1000 : 0.4,
            ease: 'easeOut'
          }}
        >
          <p className="uppercase tracking-widest text-sm opacity-80">
            {splash.text}
          </p>
        </motion.div>

        <motion.img
          src={asset.path}
          alt="Splash Logo"
          width={width}
          height={height}
          className="mt-3 drop-shadow-xl" // Removed cursor-pointer as there's no click action
          initial={{ scale: splash.popScaleFrom }}
          animate={{
            scale: shouldFadeOut ? splash.fadeOutScaleTo : [
              splash.popScaleFrom,
              splash.popOvershoot,
              splash.popScaleTo,
            ],
            opacity: shouldFadeOut ? 0 : 1
          }}
          transition={{
            duration: shouldFadeOut ? splash.fadeOutDurationMs / 1000 : splash.popDurationMs / 1000,
            times: shouldFadeOut ? undefined : [0, 0.6, 1],
            ease: 'easeOut',
          }}
        />
      </div>
    </div>
  );
}
